import { Link } from 'react-router-dom';

import { ApiKeysPanel, ApiQuickstartPanel } from '../components/keys';
import { Button } from '../components/ui/Button';
import { Note } from '../components/ui/Note';
import { Panel } from '../components/ui/Panel';
import { BILLING_LIVE, BILLING_PENDING_NOTE, PAID_PLAN_TRIAL } from '../config';
import { formatPlanPrice, isPaidPlanId, PLAN_TIERS } from '../data/plans';
import { useAccount, useBillingPortal, useCheckout } from '../hooks/useAccount';
import { useDocumentHead } from '../hooks/useDocumentHead';
import { ApiError } from '../lib/api';
import type { Plan } from '../types';

const PLAN_LABELS: Record<Plan, string> = {
    free: 'Free',
    starter: 'Starter',
    growth: 'Growth',
    scale: 'Scale',
    blocked: 'Blocked',
};

function planSummary(plan: Plan): { name: string; price: string | null; requests: string | null } {
    if (plan === 'blocked') {
        return { name: 'Blocked', price: null, requests: '0 requests / mo' };
    }
    const tier = PLAN_TIERS.find((t) => t.id === plan);
    if (!tier) return { name: PLAN_LABELS[plan], price: null, requests: null };
    return {
        name: tier.name,
        price: formatPlanPrice(tier),
        requests: tier.requests,
    };
}

function formatTrialEnds(iso: string): string {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return iso;
    }
}

export function DashboardPage() {
    useDocumentHead({
        title: 'Account console · ActualSold',
        description: 'Manage API keys and usage.',
        canonicalPath: '/app',
        noindex: true,
    });

    const { data } = useAccount();
    const portal = useBillingPortal();
    const checkout = useCheckout();

    // Only reachable once useAccount has resolved, App.tsx gates the route.
    if (!data) return null;

    const { user, quota } = data;
    const plan = planSummary(user.plan);
    // Free upgrades default to Starter; paid accounts with a Stripe customer use the portal.
    const upgradePlan = isPaidPlanId(user.plan) ? null : 'starter';
    // Stripe trial only when feature is on — free always has null after createUser;
    // ignore stale dates still on free rows from when signup defaulted trial_ends_at.
    const trialActive =
        PAID_PLAN_TRIAL &&
        isPaidPlanId(user.plan) &&
        Boolean(user.trialEndsAt) &&
        new Date(user.trialEndsAt!).getTime() > Date.now();

    return (
        <div className="space-y-6">
            <Panel title="Account">
                <p className="text-sm text-text">{user.email}</p>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    <div>
                        <p className="eyebrow mb-1 text-text-muted">Plan</p>
                        <p className="text-2xl font-bold tracking-[-0.02em] text-text">{plan.name}</p>
                        {plan.price ? (
                            <p className="mt-1 font-mono text-xs text-text-muted">{plan.price}</p>
                        ) : null}
                        {plan.requests ? (
                            <p className="mt-1 font-mono text-xs text-text-muted">{plan.requests}</p>
                        ) : null}
                        {trialActive && user.trialEndsAt ? (
                            <p className="mt-2 text-sm text-text-muted">
                                Trial ends {formatTrialEnds(user.trialEndsAt)}
                            </p>
                        ) : null}
                        {user.plan === 'blocked' ? (
                            <p className="mt-2 bg-error-soft px-2 py-1.5 text-sm text-error">
                                Payment failed. Fix billing to restore access.
                            </p>
                        ) : null}
                    </div>

                    <div>
                        <p className="eyebrow mb-1 text-text-muted">Requests this month</p>
                        <p className="text-2xl font-bold tracking-[-0.02em] text-text">
                            {quota.used.toLocaleString()}{' '}
                            <span className="text-base font-normal text-text-muted">
                                / {quota.limit.toLocaleString()}
                            </span>
                        </p>
                        <p className="mt-1 text-sm text-text-muted">
                            {quota.remaining.toLocaleString()} remaining this period
                        </p>
                    </div>
                </div>

                {quota.message ? (
                    <div className="mt-4">
                        <Note tone="warn">{quota.message}</Note>
                    </div>
                ) : null}

                {portal.error || checkout.error ? (
                    <div className="mt-4">
                        <Note tone="warn">
                            {portal.error instanceof ApiError
                                ? portal.error.message
                                : checkout.error instanceof ApiError
                                  ? checkout.error.message
                                  : 'Billing request failed.'}
                        </Note>
                    </div>
                ) : null}

                {!BILLING_LIVE ? (
                    <div className="mt-4">
                        <Note>{BILLING_PENDING_NOTE}</Note>
                    </div>
                ) : null}

                <div className="mt-5 flex flex-wrap items-center gap-3">
                    {!BILLING_LIVE ? (
                        <Link to="/pricing" className="text-sm text-primary underline">
                            See plans and join the list
                        </Link>
                    ) : user.billable ? (
                        <Button variant="secondary" size="sm" onClick={() => portal.mutate()} disabled={portal.isPending}>
                            {portal.isPending ? 'Opening…' : 'Manage billing'}
                        </Button>
                    ) : upgradePlan ? (
                        <>
                            <Button
                                size="sm"
                                onClick={() => checkout.mutate(upgradePlan)}
                                disabled={checkout.isPending}
                            >
                                {checkout.isPending ? 'Redirecting…' : 'Upgrade to Starter'}
                            </Button>
                            <Link to="/pricing" className="text-sm text-primary underline">
                                See all plans
                            </Link>
                        </>
                    ) : (
                        <Link to="/pricing" className="text-sm text-primary underline">
                            View plans
                        </Link>
                    )}
                </div>
            </Panel>

            <ApiKeysPanel />
            <ApiQuickstartPanel />
        </div>
    );
}
